import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import axios from 'axios';
import { AvisoService } from '../../../services/aviso/aviso.service';
import { Aviso } from '../../../models/Aviso';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { finalize, lastValueFrom } from 'rxjs';
import { getPaginatorIntl } from '../../../shared/mat-paginator-intl';
import { clearPaginas, pushPaginaAtiva } from '../../../shared/activePage';

@Component({
  selector: 'app-listar-avisos',
  imports: [
    MatTableModule,
    CommonModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    FormsModule,
    MatSortModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './listar-avisos.html',
  styleUrl: './listar-avisos.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: MatPaginatorIntl, useFactory: getPaginatorIntl }],
})
export class ListarAvisos implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  avisos = new MatTableDataSource<any>();
  colunasTabela = [
    { id: 'titulo', header: 'Título do aviso', propriedade: 'titulo' },
    { id: 'situacao', header: 'Situação', propriedade: 'situacao' },
    { id: 'status', header: 'Status', propriedade: 'status' },
    { id: 'dataAgendamento', header: 'Data', propriedade: 'dataAgendamento' },
  ];

  fieldText = '';
  updateFieldText(event: Event) {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.avisos.filter = query;

    this.total = this.avisos.filteredData.length;
    this.atualizarContagem(0, this.pageSize);
  }

  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;

  public isLoading = false;

  get displayedColumns(): string[] {
    return this.colunasTabela.map((coluna) => coluna.id);
  }

  constructor(
    private readonly router: Router,
    private avisoService: AvisoService
  ) {}

  ngOnInit(): void {
    clearPaginas();
    pushPaginaAtiva({
        nome: 'Home',
        rota: '/home'
    });
    pushPaginaAtiva({
        nome: 'Avisos cadastrados',
        rota: ''
    });
    this.avisos.filterPredicate = (data: any, filtro: string) => {
      const titulo = data.titulo?.toLowerCase() || '';
      return titulo.includes(filtro);
    };

    this.isLoading = true;
    this.findAllAvisos();
  }

  ngAfterViewInit(): void {
    this.avisos.paginator = this.paginator;
    this.sort.sort({
      id: 'dataAgendamento',
      start: 'desc',
      disableClear: true,
    });

    this.avisos.sort = this.sort;
    this.paginator.page.subscribe((event) => this.onPageChange(event));
  }

  async findAllAvisos() {
    try {
      const observableAvisos = this.avisoService.getAvisosDoCriador().pipe(
        finalize(() => {
          this.isLoading = false;
        })
      );

      const avisosArray = await lastValueFrom(observableAvisos);

      this.avisos.data = avisosArray.map((aviso) => {
        const statusFormatado = aviso.status
          ? aviso.status.charAt(0).toUpperCase() +
            aviso.status.slice(1).toLowerCase()
          : '-';

        return {
          ...aviso,
          status: statusFormatado,
          dataAgendamento: this.formatarData(aviso.dataAgendamento) || '-',
          totalVinculados: aviso.pessoas?.length + aviso.usuarios?.length,
        };
      });

      this.total = this.avisos.data.length;
      this.atualizarContagem(0, this.pageSize);
    } catch (err) {
      console.error('Falha ao buscar os avisos:', err);
    }
  }

  visualizarAviso(id: number) {
    this.router.navigateByUrl('avisos/detalhes/' + id);
  }

  formatarValor(aviso: any, coluna: any): string {
    if (coluna.format) {
      return coluna.format(aviso[coluna.propriedade]);
    }
    return aviso[coluna.propriedade] || '';
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number) {
    if (this.total === 0) {
      this.inicio = 0;
      this.fim = 0;
      return;
    }

    this.inicio = pageIndex * pageSize + 1;
    this.fim = Math.min((pageIndex + 1) * pageSize, this.total);
  }

  private formatarData(dataInput: Date | string): string {
    if (!dataInput) {
      return '';
    }
    const data = new Date(dataInput);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  getStatusClass(status: string): string {
    const statusNormalizado = status.toLowerCase();

    switch (statusNormalizado) {
      case 'agendado':
        return 'status agendado';
      case 'enviado':
        return 'status enviado';
      case 'rascunho':
        return 'status rascunho';
      case 'removido':
        return 'status removido';
      default:
        return 'status default';
    }
  }

  abrirTelaNovoAviso() {
    this.router.navigateByUrl('avisos/novo');
  }

  abrirDetalhesAviso(aviso: any) {
    this.router.navigateByUrl(`avisos/detalhes/${aviso.id}`);
  }
}
